import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, TextField, Button, Chip, DialogTitle, DialogContent, Badge, Divider, Alert, Skeleton,
  useMediaQuery, InputAdornment, Menu, MenuItem, Checkbox, ListItemText
} from '@mui/material';
import Dialog from '../components/AppDialog';
import { useTheme, alpha } from '@mui/material/styles';
import { green, red, blueGrey } from '@mui/material/colors';
import {
  CalendarMonth, Event, MeetingRoom, MusicNote, HourglassEmpty, Refresh, Search, FilterList
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import api from '../api/axios';
import { stripSeconds } from '../utils/timeFormat';

dayjs.locale('ko');

// 활동 유형 → 아이콘 / 의미(tone) / 라벨
// 색은 tone(예약=positive, 취소=negative, 대기=neutral) 2~3톤으로만 제한해 시각 소음을 줄인다.
const getActivityMeta = (type) => {
  switch (type) {
    case 'RESERVATION':       return { icon: Event,          tone: 'positive', label: '레슨 예약' };
    case 'CANCEL':            return { icon: Event,          tone: 'negative', label: '레슨 취소' };
    case 'PRACTICE_RESERVE':  return { icon: MeetingRoom,    tone: 'positive', label: '연습실 예약' };
    case 'PRACTICE_CANCEL':   return { icon: MeetingRoom,    tone: 'negative', label: '연습실 취소' };
    case 'LESSON_RESERVE':    return { icon: MusicNote,      tone: 'positive', label: '레슨실 예약' };
    case 'LESSON_CANCEL':     return { icon: MusicNote,      tone: 'negative', label: '레슨실 취소' };
    case 'WAITLIST_REGISTER': return { icon: HourglassEmpty, tone: 'neutral',  label: '대기 등록' };
    case 'WAITLIST_CANCEL':   return { icon: HourglassEmpty, tone: 'neutral',  label: '대기 취소' };
    default:                  return { icon: HourglassEmpty, tone: 'neutral',  label: '기타' };
  }
};

// tone → 라이트/다크 모두에서 차분하게 읽히는 색
const toneColor = (tone, theme) => {
  const dark = theme.palette.mode === 'dark';
  if (tone === 'positive') return dark ? green[400] : green[700];
  if (tone === 'negative') return dark ? red[300] : red[700];
  return dark ? blueGrey[300] : blueGrey[600];
};

// 필터 칩에 노출할 전체 유형 순서
const ALL_TYPES = [
  'RESERVATION', 'CANCEL',
  'PRACTICE_RESERVE', 'PRACTICE_CANCEL',
  'LESSON_RESERVE', 'LESSON_CANCEL',
  'WAITLIST_REGISTER', 'WAITLIST_CANCEL',
];

// 활동이 있는 날짜에 뱃지 표시
const ActivityDay = (props) => {
  const { day, outsideCurrentMonth, activityDates, ...other } = props;
  const dateStr = day.format('YYYY-MM-DD');
  const activityInfo = activityDates[dateStr];
  const hasActivity = !outsideCurrentMonth && activityInfo;

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={hasActivity ? activityInfo.count : undefined}
      color="primary"
      sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15, fontWeight: 700 } }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(hasActivity && {
            fontWeight: 700,
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.14),
            color: 'primary.main',
            '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.24) },
          })
        }}
      />
    </Badge>
  );
};

const ActivityPage = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTypes, setActiveTypes] = useState([]); // 비어있으면 전체
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activityDates, setActivityDates] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null); // 모바일 유형 필터 메뉴

  // 검색어 디바운스 (전역 서버 검색 호출 억제)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(false);
      const params = {};
      if (debouncedSearch) params.q = debouncedSearch;
      else if (selectedDate) params.date = selectedDate;
      const res = await api.get('/activities', { params });
      setActivities(res.data);
    } catch (err) {
      console.error('활동 조회 실패', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDates = async () => {
    try {
      setCalendarLoading(true);
      const res = await api.get('/activities/available-dates');
      const dateMap = {};
      res.data.forEach((item) => { dateMap[item.date] = { count: item.count }; });
      setActivityDates(dateMap);
    } catch (err) {
      console.error('날짜 목록 조회 실패', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  // 최초 진입: 미확인 목록을 먼저 불러온 뒤 읽음 처리 (순서 유지), 달력용 날짜도 로드
  useEffect(() => {
    (async () => {
      await loadActivities();
      api.post('/activities/mark-read').catch((err) => console.error('읽음 처리 실패', err));
    })();
    fetchAvailableDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 검색어/날짜 변경 시 재조회 (최초 렌더는 위 마운트 이펙트가 담당하므로 skip)
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedDate]);

  const handleOpenCalendar = () => setCalendarOpen(true);

  const handleDateSelect = (day) => {
    const dateStr = day.format('YYYY-MM-DD');
    if (!activityDates[dateStr]) return; // 활동 없는 날짜는 무시
    setSearch('');
    setDebouncedSearch('');
    setSelectedDate(dateStr);
    setCalendarOpen(false);
  };

  const handleBackToUnread = () => {
    setSelectedDate(null);
    setSearch('');
    setDebouncedSearch('');
  };

  const toggleType = (type) => {
    setActiveTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const visibleActivities = activities.filter(
    (act) => activeTypes.length === 0 || activeTypes.includes(act.type)
  );

  const formatDateLabel = (dateStr) => dayjs(dateStr).format('M월 D일 (ddd)');

  // 유형 필터 칩: 미선택은 중립(무채색), 선택 시에만 tone 색을 채워 소음 최소화
  const renderTypeFilters = () =>
    ALL_TYPES.map((type) => {
      const meta = getActivityMeta(type);
      const color = toneColor(meta.tone, theme);
      const selected = activeTypes.includes(type);
      return (
        <Chip
          key={type}
          label={meta.label}
          size="small"
          onClick={() => toggleType(type)}
          sx={{
            borderRadius: 1.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            border: '1px solid',
            borderColor: selected ? 'transparent' : 'divider',
            color: selected ? theme.palette.getContrastText(color) : 'text.secondary',
            backgroundColor: selected ? color : 'transparent',
            '&:hover': { backgroundColor: selected ? color : 'action.hover' },
          }}
        />
      );
    });

  // 달력 (데스크톱 사이드바 / 모바일 다이얼로그 공용)
  const renderCalendar = () =>
    calendarLoading ? (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    ) : (
      <DateCalendar
        onChange={handleDateSelect}
        reduceAnimations
        slots={{ day: ActivityDay }}
        slotProps={{ day: { activityDates } }}
        sx={{
          width: '100%',
          maxWidth: 360,
          mx: 'auto',
          height: { xs: 340, sm: 'auto' },
          maxHeight: 'none',
          // MUI 피커 루트(PickerViewRoot)의 기본 overflow: hidden 때문에
          // 토요일 열의 두 자리 수 뱃지(예: 14)가 오른쪽 끝에서 잘리던 문제 해결.
          // reduceAnimations로 월 전환 슬라이드를 꺼둬 잔상 없이 안전하다.
          overflow: 'visible',
          // 요일 헤더와 날짜 행을 같은 규칙으로 가로 폭에 균등 배치 → 7칸이 넘쳐 토요일이 잘리던 문제 해결
          '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
            justifyContent: 'space-between',
            px: 0.5,
          },
          // 활동일 카운트 뱃지가 셀 모서리 밖으로 조금 나가도 잘리지 않도록 오버플로 허용
          // (reduceAnimations로 월 전환 슬라이드를 꺼 잔상 없이 안전)
          '& .MuiDayCalendar-monthContainer': { overflow: 'visible' },
          '& .MuiPickersDay-root': {
            width: { xs: 34, sm: 36 },
            height: { xs: 34, sm: 36 },
            margin: 0,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            borderRadius: 1.5,
          },
          '& .MuiDayCalendar-weekDayLabel': {
            width: { xs: 34, sm: 36 },
            margin: 0,
            fontSize: '0.75rem',
            color: 'text.disabled',
          },
          '& .MuiPickersCalendarHeader-label': { fontWeight: 700 },
        }}
      />
    );

  // 활동 표시용 공통 데이터 (데스크톱 행 / 모바일 카드 공용)
  const buildMeta = (act) => {
    const meta = getActivityMeta(act.type);
    const lessonDate = act.date ? dayjs(act.date).format('M월 D일 (ddd)') : '';
    const lessonHour = act.start_time ? act.start_time.slice(0, 5) : '';
    const fallback = `${act.user?.name || '-'} · ${[lessonDate, lessonHour].filter(Boolean).join(' ')} ${meta.label}`.trim();
    return {
      label: meta.label,
      Icon: meta.icon,
      color: toneColor(meta.tone, theme),
      actionTime: dayjs(act.createdAt).format('YYYY.MM.DD (ddd) HH:mm'),
      actionDate: dayjs(act.createdAt).format('YYYY.MM.DD HH:mm'), // 요일 제외 (모바일 '활동일' 표기용)
      text: stripSeconds(act.description) || fallback,
    };
  };

  const iconTileBg = (color) => alpha(color, theme.palette.mode === 'dark' ? 0.22 : 0.12);

  // 데스크톱: 전폭 1열 로그 행 — 최신순 세로 스캔 + 가로 폭 활용
  const renderDesktopRow = (act, isLast) => {
    const m = buildMeta(act);
    const { Icon } = m;
    return (
      <Box
        key={act.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: '34px auto minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 1.5,
          px: 2, py: 1.25,
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'divider',
          transition: 'background-color .12s ease',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'grid', placeItems: 'center', backgroundColor: iconTileBg(m.color), color: m.color }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="caption" sx={{ color: m.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {m.label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={m.text} sx={{ color: 'text.primary', fontWeight: 500 }}>
            {m.text}
          </Typography>
          {act.teacher?.name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {act.teacher.name} 선생님
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          {m.actionTime}
        </Typography>
      </Box>
    );
  };

  // 모바일: 세로 카드
  const renderMobileCard = (act) => {
    const m = buildMeta(act);
    const { Icon } = m;
    return (
      <Paper key={act.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center', backgroundColor: iconTileBg(m.color), color: m.color }}>
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: m.color, fontWeight: 700, display: 'block', mb: 0.5 }}>{m.label}</Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.45 }}>{m.text}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {m.label}일 {m.actionDate}{act.teacher?.name ? ` · ${act.teacher.name} 선생님` : ''}
          </Typography>
        </Box>
      </Paper>
    );
  };

  const renderSkeletons = () => {
    if (isDesktop) {
      return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: i === 5 ? 'none' : '1px solid', borderColor: 'divider' }}>
              <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1.5 }} />
              <Skeleton width={64} height={16} />
              <Skeleton height={18} sx={{ flex: 1 }} />
              <Skeleton width={120} height={16} />
            </Box>
          ))}
        </Paper>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, display: 'flex', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="45%" height={16} />
              <Skeleton width="90%" height={22} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  // 현재 뷰 표시 (검색 / 날짜 / 미확인)
  const renderViewChip = () => {
    if (debouncedSearch) {
      return (
        <Chip label={`검색: "${debouncedSearch}"`} onDelete={handleBackToUnread} color="secondary" sx={{ mb: 2, borderRadius: 1.5 }} />
      );
    }
    if (selectedDate) {
      return (
        <Chip label={formatDateLabel(selectedDate)} onDelete={handleBackToUnread} color="primary" sx={{ mb: 2, borderRadius: 1.5 }} />
      );
    }
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        확인하지 않은 최근 변경 내역
      </Typography>
    );
  };

  const emptyMessage = debouncedSearch
    ? '검색 결과가 없습니다.'
    : selectedDate
      ? '해당 날짜에 변경 내역이 없습니다.'
      : '새로운 변경 내역이 없습니다.';

  const renderMainContent = () => {
    if (error) {
      return (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" startIcon={<Refresh />} onClick={loadActivities}>재시도</Button>}
        >
          변경 내역을 불러오지 못했습니다.
        </Alert>
      );
    }
    if (loading) return renderSkeletons();
    if (visibleActivities.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{ py: 6, px: 3, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed', color: 'text.secondary' }}
        >
          <Typography variant="body2">
            {activities.length > 0 ? '선택한 유형의 변경 내역이 없습니다.' : emptyMessage}
          </Typography>
        </Paper>
      );
    }
    return isDesktop ? (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {visibleActivities.map((act, i) => renderDesktopRow(act, i === visibleActivities.length - 1))}
      </Paper>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visibleActivities.map(renderMobileCard)}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko" dateFormats={{ monthAndYear: 'YYYY년 M월' }}>
      <Box
        px={{ xs: 2, md: 4 }}
        py={{ xs: 3, md: 4 }}
        display="flex"
        justifyContent="center"
        bgcolor="background.default"
        minHeight="100vh"
      >
        <Box sx={{ width: '100%', maxWidth: 1200, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* 데스크톱 좌측 사이드바: 달력 + 유형 필터 상시 노출 */}
          {isDesktop && (
            <Paper variant="outlined" sx={{ width: 320, flexShrink: 0, p: 2.5, borderRadius: 3, position: 'sticky', top: 72 }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing=".08em">날짜별 조회</Typography>
              <Box mt={1}>{renderCalendar()}</Box>
              <Typography variant="caption" color="text.disabled" display="block" textAlign="center" mt={1}>
                숫자가 표시된 날짜만 선택할 수 있어요
              </Typography>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing=".08em">유형 필터</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {renderTypeFilters()}
              </Box>
            </Paper>
          )}

          {/* 메인 영역 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목 영역 숨김 */}
            <Box mb={3} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-.01em' }}>
                변경 사항 기록
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                회원들의 예약·취소 내역을 최신순으로 확인합니다.
              </Typography>
            </Box>

            <TextField
              variant="outlined"
              placeholder="이름 또는 초성으로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />

            {/* 모바일 전용: 날짜별 조회 + 유형 필터(드롭다운) */}
            {!isDesktop && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<CalendarMonth />} onClick={handleOpenCalendar} sx={{ borderRadius: 2 }}>
                    날짜별 조회
                  </Button>
                  <Button
                    variant={activeTypes.length > 0 ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<FilterList />}
                    onClick={(e) => setFilterAnchor(e.currentTarget)}
                    disableElevation
                    sx={{ borderRadius: 2 }}
                  >
                    유형 필터{activeTypes.length > 0 ? ` · ${activeTypes.length}` : ''}
                  </Button>
                  {activeTypes.length > 0 && (
                    <Button size="small" onClick={() => setActiveTypes([])} sx={{ color: 'text.secondary' }}>
                      초기화
                    </Button>
                  )}
                </Box>

                {activeTypes.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {activeTypes.map((type) => {
                      const meta = getActivityMeta(type);
                      const color = toneColor(meta.tone, theme);
                      return (
                        <Chip
                          key={type}
                          label={meta.label}
                          size="small"
                          onDelete={() => toggleType(type)}
                          sx={{
                            borderRadius: 1.5,
                            fontWeight: 600,
                            color,
                            backgroundColor: alpha(color, 0.12),
                            '& .MuiChip-deleteIcon': { color: alpha(color, 0.6), '&:hover': { color } },
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </>
            )}

            {renderViewChip()}
            {renderMainContent()}
          </Box>
        </Box>
      </Box>

      {/* 모바일 유형 필터 드롭다운 */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.5, minWidth: 180 } } }}
      >
        {ALL_TYPES.map((type) => {
          const meta = getActivityMeta(type);
          const color = toneColor(meta.tone, theme);
          const checked = activeTypes.includes(type);
          return (
            <MenuItem key={type} onClick={() => toggleType(type)} dense>
              <Checkbox checked={checked} size="small" sx={{ py: 0.25, color, '&.Mui-checked': { color } }} />
              <ListItemText primary={meta.label} primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* 모바일 달력 다이얼로그 */}
      {!isDesktop && (
        <Dialog
          open={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { m: 1, borderRadius: 3, maxHeight: '95vh' } }}
        >
          <DialogTitle sx={{ py: 1.5, fontWeight: 700 }}>날짜 선택</DialogTitle>
          <DialogContent sx={{ overflow: 'visible', pb: 3, px: 1 }}>
            {renderCalendar()}
            <Typography variant="caption" color="text.disabled" textAlign="center" display="block" mt={2}>
              숫자가 표시된 날짜만 선택할 수 있어요
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </LocalizationProvider>
  );
};

export default ActivityPage;
